import apiClient from '../../../../api/axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TCouponsResponse, TCouponResponse, TCouponForm, TMenuCategory } from '../schemas';
import * as Api from '../../../../api';

const COUPONS_BASE_URL = '/coupon';
const QUERY_KEY = 'restaurant-coupons';
const CATEGORIES_QUERY_KEY = 'restaurant-menu-categories';

export const useGetCoupons = (restaurantId: string, page = 1, limit = 10) => {
  return useQuery({
    queryKey: [QUERY_KEY, restaurantId, page, limit],
    queryFn: async () => {
      const response = await apiClient.get<TCouponsResponse>(COUPONS_BASE_URL, {
        headers: { 'Restaurant-ID': restaurantId },
        params: { pageNum: page, pageSize: limit },
      });
      return {
        rows: response.data?.data?.rows || [],
        count: response.data?.data?.total || 0,
        pageNum: response.data?.data?.pageNum || 1,
        pageSize: response.data?.data?.pageSize || limit,
        pages: response.data?.data?.pages || 0,
      };
    },
    enabled: !!restaurantId,
  });
};

export const useGetCoupon = (
  restaurantId: string,
  couponId: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [QUERY_KEY, restaurantId, couponId],
    queryFn: async () => {
      const response = await apiClient.get<TCouponResponse>(`${COUPONS_BASE_URL}/${couponId}`, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data;
    },
    enabled: !!restaurantId && !!couponId && (options?.enabled !== false),
  });
};

export const useCreateCoupon = (restaurantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: TCouponForm) => {
      const response = await apiClient.post<TCouponResponse>(COUPONS_BASE_URL, data, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, restaurantId] });
    },
  });
};

export const useUpdateCoupon = (restaurantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TCouponForm> }) => {
      const response = await apiClient.put<TCouponResponse>(`${COUPONS_BASE_URL}/${id}`, data, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, restaurantId] });
    },
  });
};

export const useDeleteCoupon = (restaurantId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${COUPONS_BASE_URL}/${id}`, {
        headers: { 'Restaurant-ID': restaurantId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, restaurantId] });
    },
  });
};

export const useGetMenuCategories = (restaurantId: string) => {
  return useQuery<TMenuCategory[], Error>({
    queryKey: [CATEGORIES_QUERY_KEY, restaurantId],
    queryFn: async () => {
      const filter = `pageSize=100000&pageNum=1&filter__idRestaurant=${restaurantId}`;
      const response = await Api.getMenuCategoryList(filter, restaurantId);
      if (response.success) {
        return response.data.rows.sort((a: TMenuCategory, b: TMenuCategory) => (a?.order || 0) - (b?.order || 0));
      }
      throw new Error(response.msg || 'Failed to fetch categories');
    },
    enabled: !!restaurantId,
    retry: 1,
  });
};
